import React, { useContext, useState, createContext, useEffect } from 'react'
import AlertBallon from '../components/AlertBallon'
import Loading from '../components/Loading'
import { ApiResponse } from '../lib/api'

interface ContextProps {
  isLoading: boolean
  setIsLoading: any
  showAlert: (
    type: string,
    title: string,
    content: string,
    percentage?: number
  ) => void
  alertError: any
  stage: any
  setStage: any
  setEmail: any
  email: any
  code: any
  setCode: any
  modalStatic: boolean
  setModalStatic: React.Dispatch<React.SetStateAction<boolean>>

  apiHandler: <T, K>(params: IApiHandler<T, K>) => Promise<void | K>
}

export const GeneralContext = createContext<ContextProps>({} as ContextProps)

type IApiHandler<T, K> = {
  apiCaller: (...args: any[]) => Promise<ApiResponse<K>>
  constructData: () => Promise<T[]>
  validator?: () => boolean
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>
  onSuccess: (data: K) => Promise<string | void> | (string | void)
  avoidError?: boolean
  showNativeError?: boolean
}

export const GeneralProvider: React.FC = ({ children }: any) => {
  const [isLoading, setIsLoading] = useState(false)
  const [stage, setStage] = useState(1)
  const [email, setEmail] = useState<string>('')
  const [code, setCode] = useState<string>('')
  // TO-DO: Remove static modal
  const [modalStatic, setModalStatic] = useState(false)

  const [alertBaloonData, setAlertbaloonData] = useState({
    opened: false,
    type: 'error',
    title: 'Ocorreu um erro!',
    content: 'Tente novamente mais tarde.',
    progressPercentage: 0
  })

  const closeAlert = () => {
    setAlertbaloonData({
      ...alertBaloonData,
      opened: false
    })
  }

  const showAlert = (
    type: string,
    title: string,
    content: string,
    percentage?: number
  ) => {
    setAlertbaloonData({
      opened: true,
      type,
      title,
      content,
      progressPercentage: percentage || 0
    })
    setTimeout(closeAlert, 4000)
  }

  const alertError = (e: any) => {
    showAlert(
      'error',
      'Error',
      e?.response?.data?.message ||
        e?.response?.data?.errors?.[0] ||
        e?.response?.data?.body ||
        e?.message ||
        'We had internal problems trying to perform this operation. Please try again in a moment.'
    )
  }

  const apiHandler = async function <T, K>({
    setIsLoading: setLoading,
    apiCaller,
    constructData,
    onSuccess,
    avoidError,
    showNativeError,
    validator
  }: IApiHandler<T, K>): Promise<void | K> {
    if (validator !== undefined && !validator()) {
      return
    }
    setLoading?.(true)
    try {
      const data = await constructData()
      const { error, body } = await apiCaller(...data)
      if (error) {
        if (avoidError !== undefined && avoidError) {
          setLoading?.(false)
          return
        }

        if (showNativeError) {
          showAlert('error', 'Ocorreu um erro', body)
        } else {
          alertError(body)
        }
        setLoading?.(false)
        return
      }
      const successMessage = await onSuccess(body)
      successMessage && showAlert('', 'Sucesso', successMessage)
      setLoading?.(false)
      return body
    } catch (error) {
      // console.log({ error })
      if (avoidError !== undefined && avoidError) {
        return
      }

      showAlert(
        'error',
        'Ocorreu um erro',
        'Não conseguimos nos comunicar com o servidor'
      )
    }
    setLoading?.(false)
  }

  return (
    <GeneralContext.Provider
      value={{
        isLoading,
        setIsLoading,
        showAlert,
        alertError,
        stage,
        setStage,
        setEmail,
        email,
        code,
        setCode,
        modalStatic,
        setModalStatic,
        apiHandler
      }}
    >
      {children}
      <AlertBallon {...alertBaloonData} closeFunction={closeAlert} />
      {isLoading && <Loading />}
    </GeneralContext.Provider>
  )
}

export const useGeneral = (): ContextProps => useContext(GeneralContext)
